import L from 'leaflet'

/**
 * The aerial basemap shared by every map surface in the app, with street and
 * place labels drawn over it.
 *
 * Esri's World Imagery plus its two reference overlays — free to use with
 * attribution, which is why they are here while this is being built.
 * Production needs a deliberate decision about terms and a tile budget, and
 * that decision belongs with whoever signs for it.
 */
const IMAGERY_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'

/**
 * Transparent label overlays. Leaflet has no notion of labels of its own —
 * they belong to whatever tiles you point it at, and World Imagery is bare
 * photography — so street and place names are a second layer rather than a
 * setting.
 *
 * Both, not just roads: Transportation carries street names, and Boundaries
 * and Places carries the park and neighborhood names that tell someone which
 * property they are looking at. Drawn in that order, matching Esri's own
 * hybrid recipe, and costing under a kilobyte per tile since they are almost
 * entirely transparent.
 */
const PLACES_URL =
  'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'
const TRANSPORTATION_URL =
  'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}'

/** Credits as the services themselves report them, not as we remember them. */
const IMAGERY_ATTRIBUTION =
  'Imagery &copy; Esri, Vantor, Earthstar Geographics'
const LABELS_ATTRIBUTION =
  'Labels &copy; Esri, HERE, Garmin, &copy; OpenStreetMap contributors'

/**
 * The deepest zoom with real photography, rather than the deepest the tiling
 * scheme defines.
 *
 * Esri advertises levels to z23, but past a point it serves an identical gray
 * "Map data not yet available" placeholder — byte-for-byte the same image at
 * z22 and z23 over our sites, while z21 is genuine imagery. Requesting beyond
 * this would paint that placeholder across the map; capping here means Leaflet
 * asks for the deepest real tile and upscales instead.
 *
 * Coverage is not uniform. This is right for the suburban US sites in hand and
 * generous for dense urban areas; somewhere rural with only z18 imagery would
 * show the placeholder, and the fix is to lower this one number.
 */
const IMAGERY_MAX_NATIVE_ZOOM = 21

/**
 * Where Esri stops drawing labels at all.
 *
 * Measured, not assumed. The same tile over a suburb is 9.4 kB at z18 and 872
 * bytes at z19 — and 872 bytes is an empty transparent PNG. Esri simply has
 * nothing to say about a street once you are close enough to count the cars on
 * it.
 *
 * So above this the layers are switched off rather than requested. Nothing
 * disappears that was ever drawn; what goes away is two HTTP round trips per
 * tile position, fetching blank images, at exactly the zoom a surveyor spends
 * their day in. Three requests per position become one — which on a phone in a
 * park is latency, and latency is the whole cost.
 */
const LABELS_MAX_ZOOM = 18

/**
 * How far the map lets someone zoom, which is deliberately DEEPER than the
 * imagery goes.
 *
 * Past `IMAGERY_MAX_NATIVE_ZOOM` there is no new detail to show — Esri's
 * source over these sites is 46cm per pixel, so z21 already magnifies each real
 * pixel about sevenfold and z22 asks for a gray "not available" placeholder.
 * Leaflet upscales the deepest real tile instead of requesting that, which is
 * what `maxNativeZoom` is for.
 *
 * The extra zoom is not for seeing more. It is for HITTING things: a pin at
 * z23 is four times the span it is at z21, and placing one precisely with a
 * thumb is the job this screen exists to do. Blurrier photography under a
 * bigger target is the right trade for a survey; it would be the wrong one for
 * a map somebody is reading.
 */
const MAP_MAX_ZOOM = 23

/**
 * The pane labels are drawn into.
 *
 * Leaflet stacks `tilePane` (200), `overlayPane` (400) and `markerPane` (600).
 * A second tile layer added normally lands in `tilePane` beside the imagery,
 * where order depends on insertion — and worse, it would sit under nothing at
 * all. Its own pane at 250 puts street names above the photograph and below
 * the boundary outline and the tree pins, so "91st St" can never print across
 * a pin someone is trying to tap.
 */
const LABELS_PANE = 'swocrm-labels'
const LABELS_PANE_Z_INDEX = '250'

export const UNKNOWN_VIEW_ZOOM = 4

/**
 * The view before anything is known: the whole country, deliberately.
 *
 * Not a plausible-looking guess at a town. A map opens where its subject is —
 * the trees, the boundary, or the device — and if none of those is available
 * yet, the honest thing is to show a map that is obviously not centered on
 * anything rather than one that looks precise and is wrong.
 */
const UNKNOWN_VIEW_CENTER = L.latLng(39.5, -98.35)

/**
 * Create a map on `container` with the shared basemap and labels attached.
 *
 * Zoom controls are off: a pinch is the only zoom gesture on a phone, and the
 * +/- buttons cost two corners of a screen that is mostly map on purpose.
 */
export function createSiteMap(container: HTMLElement): L.Map {
  const map = L.map(container, {
    center: UNKNOWN_VIEW_CENTER,
    zoom: UNKNOWN_VIEW_ZOOM,
    maxZoom: MAP_MAX_ZOOM,
    zoomControl: false,
    attributionControl: true,
  })

  createAerialLayer().addTo(map)
  addLabels(map)
  return map
}

/**
 * The aerial photography on its own, for a map that is drawn as a plan and
 * offers the photo as a second view (the proposal's site explorer). Same
 * source, zoom limits and credit as `createSiteMap`, so the two cannot drift.
 */
export function createAerialLayer(): L.TileLayer {
  return L.tileLayer(IMAGERY_URL, {
    maxZoom: MAP_MAX_ZOOM,
    maxNativeZoom: IMAGERY_MAX_NATIVE_ZOOM,
    attribution: IMAGERY_ATTRIBUTION,
  })
}

/** The street and place labels, in their own pane above the photography. */
function addLabels(map: L.Map): void {
  map.createPane(LABELS_PANE)
  const pane = map.getPane(LABELS_PANE)
  if (pane !== undefined) {
    pane.style.zIndex = LABELS_PANE_Z_INDEX
    // Labels are for reading, never for hitting. Without this the overlay
    // would swallow taps meant for the map beneath it — which on the boundary
    // editor is every tap that places a corner.
    pane.style.pointerEvents = 'none'
  }

  for (const [index, url] of [PLACES_URL, TRANSPORTATION_URL].entries()) {
    L.tileLayer(url, {
      pane: LABELS_PANE,
      // Beyond this Leaflet drops the layer from view and stops asking for
      // tiles, rather than upscaling an empty one.
      maxZoom: LABELS_MAX_ZOOM,
      // Credit once. Leaflet concatenates every layer's attribution without
      // deduplicating, and the same line three times is noise.
      attribution: index === 0 ? LABELS_ATTRIBUTION : undefined,
    }).addTo(map)
  }
}
